import {
  AdGroupBidInfoSuggestion,
  AdGroupKeywordBid,
  AdGroupKeywordGroupSuggestion,
  AdGroupKeywordSuggestion,
  AdGroupTargetBid,
  CampaignCreationAccountInfo,
  CampaignCreationAdgroup,
  TargetingPredicate,
} from '@app/client/campaign-creation/shared/campaign-creation.models';
import { CriterionAdditionCriterionType } from '@app/client/criterion-addition/shared/models/criterion-addition.models';
import {
  AdGroupTargetingType,
  AdPlatform,
  AdPlatformType,
  CampaignTargetingType,
} from '@app/core/models/common.models';
import { Currency } from '@app/core/models/customer.models';
import { numberToMicros } from '@app/core/utilities/number.utilities';
import { arraySnakeToCamelCase } from '@app/core/utilities/string.utilities';
import { DefaultBidValueInEUR, TargetingBidAPIMapping, TargetingBidGroups } from './bids.constants';

export function transformation(data: any[]) {
  data = arraySnakeToCamelCase(data).map((item: any) => ({
    productId: item.adspertProductId,
    price: item.price,
    id: item.adspertProductId,
    title: item.title,
    imageUrl: item.imageUrl,
    asin: item.amzAsin,
    parentAsin: item.amzParentAsin,
    inStock: item.inStock,
    sku: item.amzSku,
  }));

  return data;
}

export function selectedDataTransformation(data: any[], allItems: any[]) {
  if (!data?.length) return data;

  return data
    .filter(({ id }) => allItems.find((e) => id === e.productId))
    .map((d) => {
      const item: any = allItems.find((e) => d.id === e.productId);
      return {
        ...d,
        productId: item.productId,
        price: item.price,
        id: item.productId,
        imageUrl: item.imageUrl,
        parentAsin: item.amzParentAsin,
        inStock: item.inStock,
      };
    });
}

export function SuggestedTargetBidValue(
  currency: Currency,
  rates: Record<Currency, number>,
  val = DefaultBidValueInEUR,
) {
  return val * rates[`EUR-${currency}`];
}

export function canCreateCampaign(account: { platform: AdPlatformType | AdPlatform }) {
  return [AdPlatformType.Amazon, AdPlatformType.Walmart].includes(account?.platform as AdPlatformType);
}

export function getTargetBidsForCreate(
  adGroup: CampaignCreationAdgroup,
  account: CampaignCreationAccountInfo,
): AdGroupTargetBid[] {
  if (account.targetingType === CampaignTargetingType.Manual) {
    const isProductTargeting = adGroup.targetingType === AdGroupTargetingType.Product;

    const targetBids = adGroup?.targetBids
      .filter((bid) =>
        isProductTargeting
          ? bid.targetExpressions.every((expression) => expression.type !== TargetingPredicate.KeywordGroupSameAs)
          : bid.targetExpressions.some((expression) => expression.type === TargetingPredicate.KeywordGroupSameAs),
      )
      .filter((bid) => bid.negative || bid.bid > 0);

    return (
      targetBids.map((bid) => ({
        ...(bid.criterionMutateId > -1 ? { criterionMutateId: bid.criterionMutateId } : {}),
        targetExpressions: bid.targetExpressions.map((expression) => ({
          value: expression.value,
          type: expression.type,
          ...(expression.label ? { name: expression.label } : {}),
        })),
        criterionType: bid.criterionType,
        ...(bid.negative ? {} : { bid: bid.bid }),
        negative: bid.negative,
      })) || []
    );
  } else {
    return TargetingBidGroups.reduce((acc, bid) => {
      if (adGroup[bid].enable)
        acc.push({
          targetExpressions: [
            {
              type: TargetingBidAPIMapping[bid],
            },
          ],
          bid: numberToMicros(adGroup[bid].value),
          negative: false,
          criterionType: CriterionAdditionCriterionType.Pat,
          ...(adGroup[bid].criterionMutateId > -1 ? { criterionMutateId: adGroup[bid].criterionMutateId } : {}),
        });
      return acc;
    }, []);
  }
}

export function getKeywordBidsForCreate(
  adGroup: CampaignCreationAdgroup,
  account: CampaignCreationAccountInfo,
): AdGroupKeywordBid[] {
  if (
    account.targetingType === CampaignTargetingType.Manual &&
    adGroup.targetingType === AdGroupTargetingType.Keyword
  ) {
    return adGroup.keywordBids
      ?.filter((bid) => bid.negative || bid.bid > 0)
      .map((bid) => ({
        ...(bid.criterionMutateId > -1 ? { criterionMutateId: bid.criterionMutateId } : {}),
        criterionName: bid.criterionName,
        criterionType: bid.criterionType,
        negative: bid.negative,
        matchType: bid.matchType,
        ...(bid.negative ? {} : { bid: bid.bid }),
      }));
  } else {
    return [];
  }
}

export function parseAdGroupsForCreate(
  adgroups: CampaignCreationAdgroup[],
  account: CampaignCreationAccountInfo,
): any[] {
  const isAmazon = account.platform === AdPlatformType.Amazon;
  const isWalmart = account.platform === AdPlatformType.Walmart;

  return adgroups.map((adgroup) => {
    const targetBids = isAmazon ? getTargetBidsForCreate(adgroup, account) : [];
    const keywordBids = isAmazon ? getKeywordBidsForCreate(adgroup, account) : [];

    const defaultMaxCpc =
      numberToMicros(adgroup.defaultBid) ||
      Math.max(...[...targetBids, ...keywordBids].map((bid) => bid.bid || 0), 10000);

    return {
      ...(adgroup.adgroupMutateId > -1 ? { adgroupMutateId: adgroup.adgroupMutateId } : {}),
      ...(adgroup.amzRetailerId ? { amzRetailerId: adgroup.amzRetailerId } : {}),
      name: adgroup.name,
      ...(isAmazon
        ? {
            defaultMaxCpc,
            targetBids,
            keywordBids,
          }
        : {}),
      products: adgroup.products.map(({ asin, sku, title, itemId, bid, id, productAdMutateId }) => {
        if (adgroup?.amzRetailerId) {
          return {
            amzRetailerId: adgroup.amzRetailerId,
            amzRetailerOfferId: id,
            name: title || id,
          };
        } else
          return {
            ...(productAdMutateId != null ? { productAdMutateId } : {}),
            name: title || id,
            ...(isAmazon ? { asin, sku } : {}),
            ...(isWalmart ? { itemId: itemId || id, bid } : {}),
          };
      }),
    };
  });
}

export function keywordSuggestionToKeywordBid(
  suggestion: AdGroupKeywordSuggestion,
  bid: AdGroupBidInfoSuggestion,
): AdGroupKeywordBid {
  return {
    criterionMutateId: -1,
    criterionName: suggestion.textLocale || suggestion.text,
    criterionType: CriterionAdditionCriterionType.Keyword,
    matchType: bid.matchType,
    bid: bid.suggestedBid?.bidMedian || suggestion?.suggestedBid?.bidMedian || suggestion?.bid || bid.bid,
    negative: false,
  };
}

export function keywordGroupSuggestionToTargetBid(suggestion: AdGroupKeywordGroupSuggestion): AdGroupTargetBid {
  return {
    targetExpressions: [{ type: TargetingPredicate.KeywordGroupSameAs, value: suggestion.id, label: suggestion.text }],
    criterionMutateId: -1,
    criterionType: CriterionAdditionCriterionType.Pat,
    negative: false,
    bid: suggestion.suggestedBid?.bidMedian,
  };
}
